import { Box, Button, IconButton, List, SwipeableDrawer } from '@mui/material'
import { OidcIdentityContext } from '@rsmus/react-auth'
import React, { useContext, useMemo, useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RsmBarIcon, CloseIcon, MenuIcon } from '@rsmus/myrsm-icons'
import { useFeatureFlag } from '../../../utils/hooks/useFeatureFlag'
import { useDeviceType } from '../../../rsmCoreComponents/hooks/useDeviceType'
import { isCemFeatureEnabled } from '../../../rsmCoreComponents/utils/featureFlagUtils'
import { Styles } from '../../../types'
import { useAppSelector } from '../../../utils/hooks'
import { Size, useWindowSize } from '../../../utils/hooks/useWindowSize'
import { MyRsmLogo, NewRsmLogo } from '../../media'

import {
  getUserInfo,
  getCemFeatures,
} from '../../../store/userInfo/userInfoSlice'
import {
  CEM_FEATURE_PAYMENT_PAY_INVOICES,
  CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS,
  CEM_FEATURE_PAYMENT_CLIENT_ADMIN,
  CEM_FEATURE_QUESTIONNAIRE_ASSIGN,
  CEM_FEATURE_QUESTIONNAIRE_EXTERNAL_COMPLETE,
  CEM_FEATURE_MANAGE_ADMIN_SETTINGS,
} from '../../../utils/constants/constants'
import RouterNavListItem from '../../Navigation/NavListItem/RouterNavListItem'
import ButtonNavListItem from '../../Navigation/NavListItem/ButtonNavListItem'
import tokens from '../../../styles/tokens.json'
import ToolBarHighContrast from '../ToolBarHighContrast'
import ToolBarHeaderName from '../ToolBarHeaderName'
import { getUserReportApps } from '../../../store/userApps/userAppsSlice'

const styles: Styles = {
  swipeableDrawer: () => ({
    zIndex: 3000,
  }),
  iconContainer: () => ({
    width: '100%',
    textAlign: 'right',
    padding: '1.25rem',
  }),
  userInfo: () => ({
    width: '100%',
    alignItems: 'center',
    paddingLeft: '1rem',
    color: '#fff',
    textAlign: 'left',
    verticalAlign: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    bgcolor: tokens.colors.black,
  }),
  list: {
    border: 'none',
    backgroundColor: tokens.colors.black,
    color: tokens.colors.white,
    'li.MuiListItem-root': {
      'a, button': {
        flex: 1,
        borderBottom: '0.0625rem solid #ccc',
        paddingX: '1rem',
        minHeight: '4rem',
        color: tokens.colors.white,
      },
      'a:hover': {
        fontFamily: tokens.type.fontFamilies.preloBold,
      },
      'a:focus-visible, button:focus-visible': {
        backgroundColor: tokens.colors.white,
        color: tokens.colors.black,
      },
    },
  },
  submenubutton: {
    display: 'flex',
    justifyContent: 'space-between',
    borderBottom: `0.0625rem solid ${tokens.colors.rsmGray.disabled}`,
    alignItems: 'center',
    width: '100%',
    minHeight: '4rem',
    padding: '1rem',
    fontSize: '1rem',
    fontWeight: 400,
    color: tokens.colors.white,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    '&:hover': {
      fontFamily: tokens.type.fontFamilies.preloBold,
    },
    '&:focus-visible': {
      backgroundColor: tokens.colors.white,
      color: tokens.colors.black,
      outline: 'none',
    },
  },
  Toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingLeft: '1rem',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    bgcolor: 'primary.main',
    gap: '1rem',
  },
  LogoContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'center',
    paddingTop: '1rem',
    paddingLeft: '1rem',
    paddingBottom: '0.5rem',
    backgroundColor: tokens.colors.black,
    backgroundImage: `url(${process.env.PUBLIC_URL}/myRSMHeaderBackground.svg)`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
  },
  TabletLogoContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'center',
    paddingTop: '1rem',
    paddingLeft: '1rem',
    paddingBottom: '0.5rem',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
  },
  CloseIcon: (theme) => ({
    '& svg': {
      fill: tokens.colors.white,
      width: '1rem',
      height: '1rem',
    },
    '& path': {
      fill: tokens.colors.white,
    },
    '&:focus-visible': {
      outline: `0.125rem solid ${tokens.colors.white}`,
      boxShadow: `0 0 0 0.125rem ${tokens.colors.black}`,
      outlineOffset: '0.125rem',
    },
    [theme.breakpoints.only('tablet')]: {
      bottom: '1rem',
      right: '2.5rem',
    },
  }),
}

type HamburgerMenuProps = {
  currentItem?: string
}

type ContactUsItem = {
  label: string
  to: string
  testId: string
  value: string
}

const HamburgerMenu = ({ currentItem = '' }: HamburgerMenuProps) => {
  const { t } = useTranslation()
  const userInfo = useAppSelector(getUserInfo)
  const userApps = useSelector(getUserReportApps)
  const cemFeatures = useAppSelector(getCemFeatures)
  const size: Size = useWindowSize()
  const { isMobile, isTablet } = useDeviceType()
  const [open, setOpen] = useState<boolean>(false)
  const [contactExpanded, setContactExpanded] = useState<boolean>(false)
  const hamburgerRef = useRef<HTMLButtonElement>(null)
  const contactButtonRef = useRef<HTMLButtonElement>(null)
  const closeIconRef = useRef<HTMLButtonElement>(null)

  const invoicingEnabled = useMemo(
    () =>
      isCemFeatureEnabled(
        [
          CEM_FEATURE_PAYMENT_PAY_INVOICES,
          CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS,
          CEM_FEATURE_PAYMENT_CLIENT_ADMIN,
        ],
        cemFeatures,
        'any',
      ),
    [userInfo, cemFeatures],
  )

  const questionnairesEnabled = useMemo(
    () =>
      isCemFeatureEnabled(
        [
          CEM_FEATURE_QUESTIONNAIRE_ASSIGN,
          CEM_FEATURE_QUESTIONNAIRE_EXTERNAL_COMPLETE,
        ],
        cemFeatures,
        'any',
      ),
    [cemFeatures],
  )

  const isAdminSettingsFlagEnabled = useFeatureFlag('AdminSettings')
  const adminSettingsEnabled = useMemo(
    () =>
      isCemFeatureEnabled(
        [CEM_FEATURE_MANAGE_ADMIN_SETTINGS],
        cemFeatures,
        'all',
      ) && isAdminSettingsFlagEnabled,
    [cemFeatures, isAdminSettingsFlagEnabled],
  )

  const engagementsEnabled = useFeatureFlag('Targeting_Engagement')
  const isUIRedesignEnabled = useFeatureFlag('UI_Redesign')
  const isInvoicingEnabled = useFeatureFlag('Invoicing')
  const isQuestionnairesEnabled = useFeatureFlag('Questionnaires')
  const isUserProfileEnabled = useFeatureFlag('UserProfile')
  const isBIReportingEnabled = useFeatureFlag('BI_Reporting')

  type ReportItem = { label: string; to: string; testId: string; value: string }

  const [reportsExpanded, setReportsExpanded] = useState<boolean>(false)
  const [reportsMenuItems, setReportsMenuItems] = useState<ReportItem[]>([])

  const isEmployee = useAppSelector(
    (state) => state.userInfo.UserInfo.isEmployee,
  )

  // Used for logout() and redirect
  const context = useContext(OidcIdentityContext)
  const handleLogoutClick = () => {
    context.logout(false)
  }

  const contactUsItems: ContactUsItem[] = [
    {
      label: t('Navigation.Support'),
      to: '/contact-us/support-page',
      testId: 'Lnk_Navigation_SupportDropdown',
      value: 'support',
    },
    {
      label: t('Navigation.Feedback'),
      to: '/contact-us/feedback',
      testId: 'Lnk_Navigation_FeedbackDropdown',
      value: 'feedback',
    },
    {
      label: t('Navigation.FAQ'),
      to: '/contact-us/faq',
      testId: 'Lnk_Navigation_FAQDropdown',
      value: 'faq',
    },
  ]

  if (adminSettingsEnabled) {
    contactUsItems.push({
      label: t('Navigation.AdminSettings'),
      to: '/contact-us/admin-settings',
      testId: 'Lnk_Navigation_AdminSettingsDropdown',
      value: 'admin-settings',
    })
  }

  useEffect(() => {
    if (hamburgerRef.current) hamburgerRef.current.focus()
  }, [open])

  useEffect(() => {
    if (open && closeIconRef.current) {
      closeIconRef.current.focus()
    }
  }, [open])

  useEffect(() => {
    if (userApps.length > 0) {
      const items: ReportItem[] = userApps.map((r, idx) => {
        const id = (r.id ?? idx + 1).toString()
        const label = r.name?.trim()?.length ? r.name!.trim() : `Report ${id}`
        const launchUri = r.launchUri?.trim() || '#'
        return {
          label,
          to: launchUri,
          testId: `Lnk_Navigation_Report_${id}`,
          value: `report-${id}`,
        }
      })
      setReportsMenuItems(items)
    } else {
      setReportsMenuItems([])
    }
  }, [isBIReportingEnabled, userApps])

  return (
    <nav>
      {!open && (
        <IconButton
          ref={hamburgerRef}
          data-testid="Btn_Header_HamburgerMenuOpen"
          size="large"
          aria-haspopup="true"
          aria-controls="main-menu-list"
          aria-expanded={open}
          aria-label={t('Navigation.OpenMainNavigationMenu')}
          tabIndex={0}
          onClick={() => setOpen((prev) => !prev)}
          color="inherit"
          sx={{
            '&:focus-visible': {
              outline: `0.125rem solid ${tokens.colors.white}`,
              boxShadow: `0 0 0 0.125rem ${tokens.colors.black}`,
              outlineOffset: '0.125rem',
            },
          }}>
          <MenuIcon
            color={isUIRedesignEnabled ? tokens.colors.white : undefined}
          />
        </IconButton>
      )}

      <SwipeableDrawer
        sx={styles.swipeableDrawer}
        PaperProps={{
          sx: {
            width: size.menuWidth,
            ...(isMobile
              ? {}
              : {
                  position: 'fixed',
                  top: '2.5rem',
                  paddingTop: '1.5rem',
                  paddingRight: '0.5rem',
                  height: 'auto',
                  boxShadow: 'none',
                  zIndex: 1100,
                  borderTop: 'none',
                  backgroundColor: 'inherit',
                }),
          },
        }}
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        onOpen={() => {}}>
        {isMobile && (
          <Box sx={styles.Toolbar}>
            <ToolBarHighContrast />
            <ToolBarHeaderName />
          </Box>
        )}

        {isMobile && (
          <Box sx={styles.LogoContainer}>
            <Box
              sx={{
                width: '5rem',
                height: 'auto',
                '& svg': {
                  width: '100%',
                  height: 'auto',
                },
              }}>
              {isUIRedesignEnabled ? (
                <NewRsmLogo titleDescription="myRSM home" />
              ) : (
                <MyRsmLogo />
              )}
            </Box>

            <IconButton
              data-testid="Btn_Header_HamburgerMenuClose"
              aria-label={t('Navigation.CloseMainNavigationMenu')}
              tabIndex={0}
              size="large"
              onClick={() => setOpen(false)}
              color="inherit"
              ref={closeIconRef}
              sx={styles.CloseIcon}>
              <CloseIcon />
            </IconButton>
          </Box>
        )}
        {isTablet && (
          <Box sx={styles.TabletLogoContainer}>
            <Box
              sx={{
                width: '0rem',
                height: 'auto',
                '& svg': {
                  width: '100%',
                  height: 'auto',
                },
              }}>
              {isUIRedesignEnabled ? (
                <NewRsmLogo titleDescription="myRSM home" />
              ) : (
                <MyRsmLogo />
              )}
            </Box>

            <IconButton
              data-testid="Btn_Header_HamburgerMenuClose"
              aria-label={t('Navigation.CloseMainNavigationMenu')}
              tabIndex={0}
              size="large"
              onClick={() => setOpen(false)}
              color="inherit"
              ref={closeIconRef}
              sx={styles.CloseIcon}>
              <CloseIcon />
            </IconButton>
          </Box>
        )}

        <Box
          sx={{ width: '100%', backgroundColor: tokens.colors.black }}
          aria-hidden="true"
          tabIndex={-1}>
          <RsmBarIcon />
        </Box>

        <Box
          component="nav"
          aria-label={t('Navigation.Main')}
          sx={{ marginTop: isMobile ? 0 : '0rem' }}>
          <List id="main-menu-list" sx={styles.list}>
            <RouterNavListItem
              to="/"
              value="home"
              isCurrent={currentItem === 'home'}
              testId="Lnk_Navigation_Home"
              onClick={() => setOpen(false)}>
              {t('Navigation.Home')}
            </RouterNavListItem>

            {engagementsEnabled && (
              <RouterNavListItem
                to="/engagements"
                value="engagements"
                isCurrent={currentItem === 'engagements'}
                testId="Lnk_Navigation_Engagements"
                onClick={() => setOpen(false)}>
                {t('Navigation.Engagements')}
              </RouterNavListItem>
            )}

            <RouterNavListItem
              to="/insights"
              value="insights"
              isCurrent={currentItem === 'insights'}
              testId="Lnk_Navigation_Insights"
              onClick={() => setOpen(false)}>
              {t('Navigation.Insights')}
            </RouterNavListItem>
            {/* PlaceHolder: Will enable once Power BI Report is compatible for Mobile     */}
            {isBIReportingEnabled && reportsMenuItems.length > 0 && (
              <>
                <li
                  style={{
                    borderBottom: `0.0625rem solid ${tokens.colors.rsmGray.disabled}`,
                  }}>
                  <Button
                    type="button"
                    aria-expanded={reportsExpanded}
                    aria-controls="reports-submenu"
                    data-testid="Lnk_Navigation_Reports"
                    onClick={() => setReportsExpanded((prev) => !prev)}
                    sx={styles.submenubutton}>
                    {t('Navigation.Reports')}
                    <span aria-hidden="true">
                      {reportsExpanded ? '▾' : '▸'}
                    </span>
                  </Button>
                </li>
                {reportsExpanded && (
                  <ul
                    id="reports-submenu"
                    role="menu"
                    tabIndex={-1}
                    style={{ padding: '0 1rem' }}>
                    {(reportsMenuItems.length > 0
                      ? reportsMenuItems
                      : [
                          {
                            label: t('Common.NoData') || 'No reports',
                            to: '#',
                            testId: 'Lnk_Navigation_Report_None',
                            value: 'no-reports',
                          } as ReportItem,
                        ]
                    ).map((item) => (
                      <li key={item.value} role="none">
                        <RouterNavListItem
                          to={item.to}
                          value={item.value}
                          testId={item.testId}
                          isCurrent={currentItem === item.value}
                          onClick={() => setOpen(false)}>
                          {item.label}
                        </RouterNavListItem>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
            {isInvoicingEnabled && invoicingEnabled && (
              <RouterNavListItem
                to="/invoicing"
                value="invoicing"
                isCurrent={currentItem === 'invoicing'}
                testId="Lnk_Navigation_Invoicing"
                onClick={() => setOpen(false)}>
                {t('Navigation.PayMyBill')}
              </RouterNavListItem>
            )}
            {isQuestionnairesEnabled && questionnairesEnabled && (
              <RouterNavListItem
                to="/questionnaires"
                value="questionnaires"
                isCurrent={currentItem === 'questionnaires'}
                testId="Lnk_Navigation_Questionnaires"
                onClick={() => setOpen(false)}>
                {t('Navigation.Questionnaire.MyQuestionnaires')}
              </RouterNavListItem>
            )}
            <li
              style={{
                borderBottom: `0.0625rem solid ${tokens.colors.rsmGray.disabled}`,
              }}>
              <Button
                type="button"
                aria-expanded={contactExpanded}
                aria-controls="contact-us-submenu"
                data-testid="Lnk_Navigation_contact-us"
                onClick={() => setContactExpanded((prev) => !prev)}
                ref={contactButtonRef}
                sx={styles.submenubutton}>
                {t('Navigation.ContactUs')}
                <span aria-hidden="true">{contactExpanded ? '▾' : '▸'}</span>
              </Button>
            </li>
            {contactExpanded && (
              <ul
                id="contact-us-submenu"
                role="menu"
                tabIndex={-1}
                style={{ padding: '0 1rem' }}>
                {contactUsItems.map((item) => (
                  <li key={item.to} role="none">
                    <RouterNavListItem
                      to={item.to}
                      value={item.value}
                      testId={item.testId}
                      isCurrent={currentItem === item.value}
                      onClick={() => setOpen(false)}>
                      {item.label}
                    </RouterNavListItem>
                  </li>
                ))}
              </ul>
            )}

            {isUserProfileEnabled && isMobile && (
              <RouterNavListItem
                to="/profile"
                value="profile"
                isCurrent={currentItem === 'profile'}
                testId="Lnk_Navigation_Profile"
                onClick={() => setOpen(false)}>
                {t('Navigation.Profile')}
              </RouterNavListItem>
            )}

            {!isEmployee && isMobile && (
              <ButtonNavListItem
                testId="Lnk_Navigation_Logout"
                onClick={handleLogoutClick}>
                {t('Navigation.Logout')}
              </ButtonNavListItem>
            )}
          </List>
        </Box>
      </SwipeableDrawer>
    </nav>
  )
}

export default HamburgerMenu
